import React from 'react';
import { FileText, Clock, CheckCircle2, AlertTriangle, Phone, MessageSquare, GraduationCap, Zap, TrendingDown, ShieldAlert, Ban } from 'lucide-react';
import { motion } from 'framer-motion';
export function PointSystem() {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} className="space-y-6" data-id="element-450">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-451">
        <div className="bg-[#0a1e5e] px-6 py-5" data-id="element-452">
          <h1 className="text-2xl font-extrabold text-white tracking-tight" data-id="element-453">
            Point System Reference
          </h1>
          <p className="text-blue-200 text-sm mt-1 font-medium" data-id="element-454">
            Complete guide to earning points, bonuses, and avoiding deductions.
          </p>
        </div>

        <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-455">
          {/* Core Production */}
          <Section title="Core Production" icon={<FileText className="w-5 h-5 text-blue-600" data-id="element-457" />} color="blue" data-id="element-456">
            <Rule label="Credit Pull" points={750} data-id="element-458" />
            <Rule label="Doc Out Sent" points={500} data-id="element-459" />
            <Rule label="Submission" points={500} data-id="element-460" />
            <Rule label="Closing" points={250} data-id="element-461" />
          </Section>

          {/* Speed & Quality */}
          <Section title="Speed & Quality Bonuses" icon={<Zap className="w-5 h-5 text-orange-500" data-id="element-463" />} color="orange" data-id="element-462">
            <Rule label="Speed Bonus" desc="Loan closes within 14 days of Credit Pull" points={1500} data-id="element-464" />
            <Rule label="Doc Out Signing Bonus" desc="Signed within 3 hours of being sent" points={250} data-id="element-465" />
            <div className="mt-4 pt-4 border-t border-gray-100" data-id="element-466">
              <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3" data-id="element-467">
                Submission Quality
              </h4>
              <div className="space-y-3" data-id="element-468">
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-100" data-id="element-469">
                  <p className="text-sm font-bold text-gray-800 mb-2" data-id="element-470">UWM</p>
                  <div className="space-y-1" data-id="element-471">
                    <Rule label="3 or fewer conditions" points={500} compact data-id="element-472" />
                    <Rule label="10 or more conditions" points={-500} compact data-id="element-473" />
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-100" data-id="element-474">
                  <p className="text-sm font-bold text-gray-800 mb-1" data-id="element-475">
                    All Other Lenders
                  </p>
                  <p className="text-[10px] text-gray-500 mb-2 leading-tight" data-id="element-476">
                    Village Capital, EPM, Plaza, etc.
                  </p>
                  <div className="space-y-1" data-id="element-477">
                    <Rule label="5 or fewer conditions" points={500} compact data-id="element-478" />
                    <Rule label="12 or more conditions" points={-500} compact data-id="element-479" />
                  </div>
                </div>
              </div>
            </div>
          </Section>

          {/* Response Time */}
          <Section title="Response Time (Dialpad & Teli AI)" icon={<MessageSquare className="w-5 h-5 text-green-600" data-id="element-481" />} color="green" data-id="element-480">
            <div className="mb-4 text-xs text-gray-600 bg-green-50 p-3 rounded-lg border border-green-100" data-id="element-482">
              Measured as a monthly average for current and past clients. Missed
              calls returned within 10 minutes count as answered.
            </div>

            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-483">
              Call Answer Rate
            </h4>
            <div className="space-y-1 mb-4" data-id="element-484">
              <Rule label="95%+ Answer Rate" points={1000} compact data-id="element-485" />
              <Rule label="90%+ Answer Rate" points={500} compact data-id="element-486" />
              <Rule label="80%+ Answer Rate" points={250} compact data-id="element-487" />
              <Rule label="Below 80%" points={0} compact data-id="element-488" />
              <Rule label="Below 70%" points={-1000} compact data-id="element-489" />
            </div>

            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-490">
              Text Response Time
            </h4>
            <div className="space-y-1 mb-4" data-id="element-491">
              <Rule label="Under 5 Minutes" points={500} compact data-id="element-492" />
              <Rule label="Over 10 Minutes" points={-500} compact data-id="element-493" />
            </div>

            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-494">
              Teli AI Response Time
            </h4>
            <p className="text-[10px] text-gray-500 mb-2 leading-tight" data-id="element-495">
              Tracks leads inside Teli AI before conversion to Dialpad.
            </p>
            <div className="space-y-1" data-id="element-496">
              <Rule label="Under 5 Minutes" points={500} compact data-id="element-497" />
              <Rule label="Over 10 Minutes" points={0} compact data-id="element-498" />
            </div>
          </Section>

          {/* Activity & Loan Partner */}
          <div className="space-y-8" data-id="element-499">
            <Section title="Dial Activity" icon={<Phone className="w-5 h-5 text-purple-600" data-id="element-501" />} color="purple" data-id="element-500">
              <Rule label="Per Dial" desc="Rewards activity, but dials alone cannot reach scorecard goal." points={1} data-id="element-502" />
            </Section>

            <Section title="Loan Partner Requirements" icon={<GraduationCap className="w-5 h-5 text-teal-600" data-id="element-504" />} color="teal" data-id="element-503">
              <Rule label="Daily SAFE Practice Test" desc="Submitted to HR daily" points={25} data-id="element-505" />
              <Rule label="SAFE Exam Passed" desc="On first attempt (One-time bonus)" points={2500} data-id="element-506" />
            </Section>
          </div>

          {/* Penalties & QC */}
          <div className="lg:col-span-2" data-id="element-507">
            <Section title="Quality Control & Penalties" icon={<ShieldAlert className="w-5 h-5 text-red-600" data-id="element-509" />} color="red" data-id="element-508">
              <div className="grid md:grid-cols-3 gap-4" data-id="element-510">
                <div className="bg-red-50 border border-red-100 rounded-lg p-4" data-id="element-511">
                  <div className="flex items-center gap-2 mb-2" data-id="element-512">
                    <AlertTriangle className="w-4 h-4 text-red-600" data-id="element-513" />
                    <h4 className="text-sm font-bold text-red-900" data-id="element-514">
                      Credit Pull QC
                    </h4>
                  </div>
                  <p className="text-xs text-red-800 mb-3 leading-snug" data-id="element-515">
                    3 consecutive Credit Pulls without sending a Doc Out.
                    Counter resets when a Doc Out is sent.
                  </p>
                  <span className="inline-block bg-white px-2 py-1 rounded text-sm font-extrabold text-red-600 shadow-sm" data-id="element-516">
                    -500 PTS
                  </span>
                </div>

                <div className="bg-red-50 border border-red-100 rounded-lg p-4" data-id="element-517">
                  <div className="flex items-center gap-2 mb-2" data-id="element-518">
                    <TrendingDown className="w-4 h-4 text-red-600" data-id="element-519" />
                    <h4 className="text-sm font-bold text-red-900" data-id="element-520">
                      Files Not Moving
                    </h4>
                  </div>
                  <p className="text-xs text-red-800 mb-3 leading-snug" data-id="element-521">
                    Files remaining in the same status without a stage change.
                  </p>
                  <span className="inline-block bg-white px-2 py-1 rounded text-sm font-extrabold text-red-600 shadow-sm" data-id="element-522">
                    -50 PTS / Day
                  </span>
                </div>

                <div className="bg-red-50 border border-red-100 rounded-lg p-4" data-id="element-523">
                  <div className="flex items-center gap-2 mb-2" data-id="element-524">
                    <ShieldAlert className="w-4 h-4 text-red-600" data-id="element-525" />
                    <h4 className="text-sm font-bold text-red-900" data-id="element-526">
                      Suspended File
                    </h4>
                  </div>
                  <p className="text-xs text-red-800 mb-3 leading-snug" data-id="element-527">
                    File suspended by Underwriting.
                  </p>
                  <span className="inline-block bg-white px-2 py-1 rounded text-sm font-extrabold text-red-600 shadow-sm" data-id="element-528">
                    -500 PTS
                  </span>
                </div>

                <div className="bg-red-50 border border-red-100 rounded-lg p-4" data-id="element-529">
                  <div className="flex items-center gap-2 mb-2" data-id="element-530">
                    <Ban className="w-4 h-4 text-red-600" data-id="element-531" />
                    <h4 className="text-sm font-bold text-red-900" data-id="element-532">
                      File Withdrawn
                    </h4>
                  </div>
                  <p className="text-xs text-red-800 mb-3 leading-snug" data-id="element-533">
                    Loan file withdrawn after credit pull.
                  </p>
                  <span className="inline-block bg-white px-2 py-1 rounded text-sm font-extrabold text-red-600 shadow-sm" data-id="element-534">
                    -500 PTS
                  </span>
                </div>
              </div>
            </Section>
          </div>
        </div>
      </div>
    </motion.div>;
}
function Section({
  title,
  icon,
  children,
  color
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  color: 'blue' | 'orange' | 'green' | 'purple' | 'teal' | 'red';
}) {
  const colorMap = {
    blue: 'border-blue-100',
    orange: 'border-orange-100',
    green: 'border-green-100',
    purple: 'border-purple-100',
    teal: 'border-teal-100',
    red: 'border-red-100'
  };
  return <div className={`border-2 rounded-xl p-5 ${colorMap[color]}`} data-id="element-535">
      <div className="flex items-center gap-2 mb-4" data-id="element-536">
        {icon}
        <h3 className="text-lg font-extrabold text-gray-900" data-id="element-537">{title}</h3>
      </div>
      <div data-id="element-538">{children}</div>
    </div>;
}
function Rule({
  label,
  desc,
  points,
  compact = false
}: {
  label: string;
  desc?: string;
  points: number;
  compact?: boolean;
}) {
  const isPositive = points > 0;
  const isNegative = points < 0;
  const isZero = points === 0;
  return <div className={`flex items-center justify-between gap-4 ${compact ? 'py-1.5' : 'py-3 border-b border-gray-100 last:border-0'}`} data-id="element-539">
      <div className="min-w-0" data-id="element-540">
        <p className={`${compact ? 'text-sm' : 'text-base'} font-bold text-gray-800 leading-tight`} data-id="element-541">
          {label}
        </p>
        {desc && <p className="text-xs text-gray-500 mt-0.5 leading-snug" data-id="element-542">{desc}</p>}
      </div>
      <div className={`shrink-0 font-extrabold ${compact ? 'text-sm' : 'text-lg'} ${isPositive ? 'text-green-600' : isNegative ? 'text-red-600' : 'text-gray-400'}`} data-id="element-543">
        {isPositive ? '+' : ''}
        {points.toLocaleString()}
      </div>
    </div>;
}